/** @jsx jsx */
import { jsx } from '@emotion/core'
import { ProcedimentoOdontoSelectModel } from 'components/form/field/select/ProcedimentoOdontoSelectField'
import { FormApi } from 'final-form'
import createDecorator from 'final-form-calculate'
import { Form, FormProps } from 'react-final-form'
import { metaPath } from 'util/metaPath'
import { createValidator, ErrorObject, maxLength, required } from 'util/validation'

import { CaracteristicasLesaoFieldModel } from './components/caracteristicas-lesao/CaracteristicasLesaoField'
import { LesaoFundamentalFieldModel } from './components/caracteristicas-lesao/components/LesaoFundamentalField'
import { SintamotologiaFieldModel } from './components/caracteristicas-lesao/components/SintamotologiaField'
import { LocalTecidosMolesFieldModel } from './components/LocalTecidosMolesField'
import { TecidosMolesFieldModel } from './TecidosMolesField'

export interface TecidosMolesFormModel extends Omit<TecidosMolesFieldModel, '_id'> {
  groupErrors?: any
}

export interface TecidosMolesFormProps extends Omit<FormProps<TecidosMolesFormModel>, 'validate' | 'render'> {
  render: FormProps<TecidosMolesFormModel>['render']
  onCancel?(): void
}

const meta = metaPath<TecidosMolesFormModel>()

const validator = createValidator<TecidosMolesFormModel>(
  {
    local: [required],
    observacao: [maxLength(400)],
    caracteristicasLesao: createValidator<CaracteristicasLesaoFieldModel>({
      lesaoFundamental: createValidator<LesaoFundamentalFieldModel>({}, (values, errors) => {
        if (values?.value) {
          errors.lesaoEnvolvida = required(values?.lesaoEnvolvida)
        }

        return errors
      }),
      sintamotologia: createValidator<SintamotologiaFieldModel>({}, (values, errors) => {
        if (values?.value === 'outros') {
          errors.descricaoOutro = required(values?.descricaoOutro)
        }

        return errors
      }),
    }),
  },
  (values: TecidosMolesFormModel, errors: ErrorObject<TecidosMolesFormModel>) => {
    if (required(values.problema || values.procedimentos?.length > 0 || values.caracteristicasLesao)) {
      errors.groupErrors =
        'É obrigatório o preenchimento de pelo menos Problema/condição (CID 10), Procedimentos ou Características da lesão'
    }

    return errors
  }
)

const decorator = createDecorator(
  {
    field: meta.procedimentos.absolutePath(),
    updates: {
      [meta.observacao.absolutePath()]: (value: ProcedimentoOdontoSelectModel[], allValues: TecidosMolesFormModel) => {
        if (value?.length > 0) return allValues.observacao
      },
    },
  },
  {
    field: meta.local.absolutePath(),
    updates: {
      [meta.problema.absolutePath()]: (
        value: LocalTecidosMolesFieldModel,
        allValues: TecidosMolesFormModel,
        prevValues: TecidosMolesFormModel
      ) => {
        if (value?.value && value?.value === prevValues?.local?.value) return allValues.problema
      },
    },
  }
)

export default function TecidosMolesForm(props: TecidosMolesFormProps) {
  const { render, onSubmit, ...rest } = props

  const handleSubmit = (values: TecidosMolesFormModel, formApi: FormApi<TecidosMolesFormModel>) => {
    onSubmit(
      {
        ...values,
        observacao: values?.observacao?.trim() ? values.observacao : undefined,
      } as TecidosMolesFormModel,
      formApi
    )
    setTimeout(() => {
      formApi.reset()
    })
  }

  return (
    <Form<TecidosMolesFormModel>
      validate={validator}
      decorators={[decorator]}
      render={render}
      onSubmit={handleSubmit}
      {...rest}
    />
  )
}
